import {
	DndContext,
	DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/utils/styles';

import {
	VerticalReorderingListItemProps,
	VerticalReorderingListProps,
} from './vertical-reordering-list.types';

export function VerticalReorderingList<T>({
	items,
	onReorder,
	renderItem,
	getItemId,
	className,
	scrollThreshold = 0.2,
	scrollSpeed = 5,
}: VerticalReorderingListProps<T>) {
	const [itemsCopy, setItemsCopy] = useState<T[]>(items);
	// Prevents the useEffect from overwriting locally reordered items with the stale parent prop
	const skipNextSync = useRef(false);

	// Configure sensors for mouse, touch, and keyboard interactions
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			const oldIndex = itemsCopy.findIndex((item) => getItemId(item) === active.id);
			const newIndex = itemsCopy.findIndex((item) => getItemId(item) === over.id);

			const reorderedItems = arrayMove(itemsCopy, oldIndex, newIndex);

			skipNextSync.current = true;
			setItemsCopy(reorderedItems);
			onReorder(reorderedItems, itemsCopy[oldIndex]);
		}
	};

	useEffect(() => {
		if (skipNextSync.current) {
			skipNextSync.current = false;
			return;
		}
		setItemsCopy(items);
	}, [items]);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			modifiers={[restrictToVerticalAxis, restrictToParentElement]}
			autoScroll={{
				threshold: {
					x: scrollThreshold,
					y: scrollThreshold,
				},
				acceleration: scrollSpeed,
			}}
			onDragEnd={handleDragEnd}
		>
			<SortableContext
				items={itemsCopy.map((item) => getItemId(item))}
				strategy={verticalListSortingStrategy}
			>
				<div className={cn('h-full w-full', className)}>
					{itemsCopy.map((item, index) => renderItem(item, index))}
				</div>
			</SortableContext>
		</DndContext>
	);
}

export function VerticalReorderingListItem({
	id,
	children,
	className,
	moveIconClassName,
	withBoxShadow = true,
	hideDragIcon = false,
	removeDragIcon = false,
	iconSize = 20,
}: VerticalReorderingListItemProps) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id,
		transition: {
			duration: 250,
			easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
		},
	});

	const style = {
		transform: CSS.Transform.toString({
			x: transform?.x ?? 0,
			y: transform?.y ?? 0,
			scaleX: 1,
			scaleY: 1,
		}),
		transition,
		zIndex: isDragging ? 999999 : undefined,
		boxShadow:
			withBoxShadow && isDragging ? 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px' : undefined,
	};

	return (
		<div
			ref={setNodeRef}
			style={{ ...style }}
			className={cn(className)}
		>
			{removeDragIcon ? null : hideDragIcon ? (
				<div className={cn(moveIconClassName, 'w-[20px]')} />
			) : (
				<div
					{...attributes}
					{...listeners}
					className={cn('z-20 cursor-move', moveIconClassName)}
				>
					<GripVertical size={iconSize} />
				</div>
			)}
			{children}
		</div>
	);
}
